/**
 * Handoff Premium → Pro: quando o pedido é de âmbito nacional/regional, o
 * Premium não deve pedir "uma cidade". Oferece a passagem para o Pro
 * preservando tudo o que o utilizador já escreveu — sem reescrever nada e
 * sem consumir créditos.
 */

export type PlanHandoff = {
  prompt: string;
  scopeName: string;
  scopeKind: "country" | "region" | "multi_city";
  countryCode?: string;
  /** Duração exata pedida no texto — nunca arredondada nem substituída. */
  days?: number;
  interests?: string[];
  /** Transporte global seguro para deslocações interurbanas (nunca "walk"). */
  transport?: string;
  startDate?: string;
  endDate?: string;
  budget?: string;
  /** Preferência de alojamento já recolhida no Premium (ex.: "budget"). */
  accommodation?: string;
  /** Experiência escolhida (ex.: comboio panorâmico, roteiro de lagos). */
  experience?: string;
  /** Cidades coerentes derivadas do planeador regional — nunca o país inteiro como 1 cidade. */
  cities?: string[];
  /** Corredor curado obrigatório (Nacional 2, Route 66, NC500…). */
  corridorId?: string;
  /** Distribuição vinculativa preservada no handoff Premium → Pro. */
  multiDestinationPlan?: import("./pro/multi-destination").MultiDestinationPlan;
  createdAt: number;
};

const KEY = "routify:plan-handoff";
const MAX_AGE_MS = 30 * 60 * 1000;

function storage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function savePlanHandoff(handoff: Omit<PlanHandoff, "createdAt">): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(KEY, JSON.stringify({ ...handoff, createdAt: Date.now() } satisfies PlanHandoff));
  } catch {
    /* sessão indisponível — o handoff é opcional */
  }
}

export function readPlanHandoff(options: { consume?: boolean } = {}): PlanHandoff | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PlanHandoff;
    if (options.consume) store.removeItem(KEY);
    if (!parsed?.prompt || !Number.isFinite(parsed.createdAt)) return null;
    if (Date.now() - parsed.createdAt > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPlanHandoff(): void {
  storage()?.removeItem(KEY);
}

type Copy = {
  title: (scope: string) => string;
  body: (scope: string) => string;
  pro: (scope: string) => string;
  city: string;
  hint: string;
};

const COPY: Record<string, Copy> = {
  pt: {
    title: (scope) => `${scope} é um destino grande`,
    body: (scope) =>
      `Um roteiro por ${scope} envolve várias bases e deslocações entre regiões. Como queres avançar?`,
    pro: (scope) => `Criar roteiro por ${scope} com o Pro`,
    city: "Escolher uma cidade (Premium)",
    hint: "Ainda não usámos créditos — o teu pedido é mantido tal como o escreveste.",
  },
  en: {
    title: (scope) => `${scope} is a large destination`,
    body: (scope) =>
      `A trip across ${scope} needs several bases and inter-regional travel. How would you like to continue?`,
    pro: (scope) => `Plan ${scope} with Pro`,
    city: "Pick a single city (Premium)",
    hint: "No credits used yet — your request is kept exactly as written.",
  },
  es: {
    title: (scope) => `${scope} es un destino amplio`,
    body: (scope) =>
      `Un itinerario por ${scope} necesita varias bases y desplazamientos entre regiones. ¿Cómo quieres seguir?`,
    pro: (scope) => `Planificar ${scope} con Pro`,
    city: "Elegir una ciudad (Premium)",
    hint: "Aún no usamos créditos — tu petición se mantiene tal cual.",
  },
  fr: {
    title: (scope) => `${scope} est une destination vaste`,
    body: (scope) =>
      `Un itinéraire à travers ${scope} demande plusieurs bases et des trajets entre régions. Comment continuer ?`,
    pro: (scope) => `Planifier ${scope} avec Pro`,
    city: "Choisir une ville (Premium)",
    hint: "Aucun crédit utilisé — ta demande est conservée telle quelle.",
  },
  it: {
    title: (scope) => `${scope} è una destinazione ampia`,
    body: (scope) =>
      `Un itinerario attraverso ${scope} richiede più basi e spostamenti tra regioni. Come vuoi procedere?`,
    pro: (scope) => `Pianifica ${scope} con Pro`,
    city: "Scegliere una città (Premium)",
    hint: "Nessun credito usato — la tua richiesta resta invariata.",
  },
  de: {
    title: (scope) => `${scope} ist ein großes Reiseziel`,
    body: (scope) =>
      `Eine Route durch ${scope} braucht mehrere Standorte und Fahrten zwischen Regionen. Wie möchtest du fortfahren?`,
    pro: (scope) => `${scope} mit Pro planen`,
    city: "Eine Stadt wählen (Premium)",
    hint: "Noch kein Guthaben verbraucht — deine Anfrage bleibt unverändert.",
  },
};

export type ScopeChoiceCopy = {
  title: string;
  body: string;
  proLabel: string;
  cityLabel: string;
  hint: string;
};

export function scopeChoiceCopy(scopeName: string, language = "pt"): ScopeChoiceCopy {
  const copy = COPY[language] ?? COPY.en;
  return {
    title: copy.title(scopeName),
    body: copy.body(scopeName),
    proLabel: copy.pro(scopeName),
    cityLabel: copy.city,
    hint: copy.hint,
  };
}

// ---------------------------------------------------------------------------
// Construção do contrato completo a partir do texto já extraído no Premium
// ---------------------------------------------------------------------------

import { extractTripRequirements, requiredGlobalTransport } from "./trip-requirements";
import { planRegionalSkeleton, citiesFromRegionalSkeleton, type RegionalTransport } from "./pro/regional-planner";
import type { RegionalBase } from "./catalog/regions";
import { parseMultiDestinationPlan } from "./pro/multi-destination";

export type PlanHandoffInput = {
  prompt: string;
  scopeName: string;
  scopeKind: "country" | "region" | "multi_city";
  countryCode?: string;
  interests?: string[];
  /** Transporte atualmente selecionado no Premium (pode ser "walk"). */
  transport?: "walk" | "bike" | "car" | "transit";
  startDate?: string;
  endDate?: string;
  budget?: string;
  accommodation?: string;
  experience?: string;
  preferAffordable?: boolean;
  /** Cidades já determinadas por uma rota conhecida (extremos oficiais). */
  routeCities?: string[];
  /** Dias mínimos realistas dessa rota (nunca reduzem o pedido do utilizador). */
  minDays?: number;
  /** Corredor curado obrigatório associado ao pedido. */
  corridorId?: string;
};


const THEME_TO_REGIONAL_BASE_THEME: Record<string, RegionalBase["themes"][number]> = {
  lakes: "lakes",
  nature: "nature",
  scenic_rail: "scenic_rail",
  coast: "coast",
  food: "food",
  cultural: "cultural",
};

/**
 * Monta o contrato completo para o Pro sem reescrever nada do que o
 * utilizador já disse: duração exata, cidades coerentes (nunca o país como
 * cidade única) e transporte interurbano nunca "walk".
 */
export function buildPlanHandoff(input: PlanHandoffInput): Omit<PlanHandoff, "createdAt"> {
  const requirements = extractTripRequirements(input.prompt, { scopeText: input.scopeName });
  const multiDestinationPlan = parseMultiDestinationPlan(input.prompt);
  const days = multiDestinationPlan?.totalDays ?? requirements.requestedDays;

  const safeTransport = requiredGlobalTransport(requirements, input.transport ?? "car");
  const regionalTransport: RegionalTransport = safeTransport === "transit" ? "transit" : safeTransport;

  const themes = requirements.preferredThemes
    .map((theme) => THEME_TO_REGIONAL_BASE_THEME[theme])
    .filter((theme): theme is RegionalBase["themes"][number] => Boolean(theme));

  const regionalPlan = planRegionalSkeleton({
    countryCode: input.countryCode,
    scopeName: input.scopeName,
    days: days ?? 3,
    themes: themes.length ? themes : undefined,
    transport: regionalTransport,
    preferAffordable:
      input.preferAffordable ?? requirements.accommodationRequest?.priceSensitivity === "budget",
  });
  const cities = citiesFromRegionalSkeleton(regionalPlan);

  const interests =
    input.interests?.length
      ? input.interests
      : requirements.preferredThemes.length
        ? requirements.preferredThemes
        : undefined;

  const budget =
    input.budget ?? (requirements.accommodationRequest?.priceSensitivity === "budget" ? "budget" : undefined);

  const accommodation =
    input.accommodation ??
    (requirements.accommodationRequest ? requirements.accommodationRequest.priceSensitivity : undefined);

  // Uma rota conhecida traz geografia e duração próprias: os extremos oficiais
  // ganham ao esqueleto regional e os dias nunca ficam abaixo do realista.
  const routeCities = (input.routeCities ?? []).filter(Boolean);
  const finalCities = multiDestinationPlan
    ? multiDestinationPlan.stops.map((stop) => stop.destination)
    : routeCities.length
      ? routeCities
      : cities;
  const finalDays = input.minDays ? Math.max(days ?? 0, input.minDays) : days;

  return {
    prompt: input.prompt,
    scopeName: input.scopeName,
    scopeKind: input.scopeKind,
    countryCode: input.countryCode,
    days: finalDays,
    interests,
    transport: safeTransport,
    startDate: input.startDate,
    endDate: input.endDate,
    budget,
    accommodation,
    experience: input.experience,
    cities: finalCities.length ? finalCities : undefined,
    corridorId: input.corridorId,
    multiDestinationPlan: multiDestinationPlan ?? undefined,
  };
}


export type PlanHandoffSummary = {
  scopeLabel: string;
  days?: number;
  cities: string[];
  transportLabel: string;
  interests: string[];
  budget?: string;
  accommodation?: string;
  experience?: string;
};

const TRANSPORT_LABELS: Record<string, Record<string, string>> = {
  pt: { walk: "A pé", bike: "Bicicleta", car: "Carro", transit: "Transportes públicos" },
  en: { walk: "Walking", bike: "Bike", car: "Car", transit: "Public transport" },
  es: { walk: "A pie", bike: "Bicicleta", car: "Coche", transit: "Transporte público" },
  fr: { walk: "À pied", bike: "Vélo", car: "Voiture", transit: "Transports publics" },
  it: { walk: "A piedi", bike: "Bicicletta", car: "Auto", transit: "Trasporto pubblico" },
  de: { walk: "Zu Fuß", bike: "Fahrrad", car: "Auto", transit: "Öffentliche Verkehrsmittel" },
};

/**
 * Resumo estruturado para a confirmação na UI — o utilizador vê tudo o que
 * já escreveu sem ter de reescrever nada.
 */
export function summarizePlanHandoff(handoff: PlanHandoff, language = "pt"): PlanHandoffSummary {
  const labels = TRANSPORT_LABELS[language] ?? TRANSPORT_LABELS.en;
  return {
    scopeLabel: handoff.scopeName,
    days: handoff.days,
    cities: handoff.cities ?? [],
    transportLabel: (handoff.transport && labels[handoff.transport]) || labels.transit,
    interests: handoff.interests ?? [],
    budget: handoff.budget,
    accommodation: handoff.accommodation,
    experience: handoff.experience,
  };
}
